import { Box, Tooltip } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { LuX } from 'react-icons/lu'

import CustomChip from '@/components/Chip'
import HtmlKonvaElement from '@/components/HtmlKonvaElement'
import Icon from '@/components/Icon'
import PieChart from '@/components/PieChart'
import { useAppDispatch } from '@/hooks/redux'
import { getInitials } from '@/pages/stainingStudio/utils'
import { useAppSelector } from '@/store'
import { removeOpaquerTarget, setOpaquerRecipeOutput } from '@/store/pfmCrown'
import { OPAQUER_BACKGROUNDS } from '@/utils/constants'
import { getTooltipPlacement } from '@/utils/helper'

import LabelValuePair from './components/LabelValuePair'

const lastTooltipMap: Record<string, { x: number; y: number }> = {}

const TooltipContent = React.memo(
  ({ output, setTooltipOpen }: { output: any; setTooltipOpen: () => void }) => {
    return (
      <Box
        display="flex"
        flexGrow={1}
        flexDirection="column"
        gap={1}
        alignItems="flex"
      >
        {output?.provided_input?.brand && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            <LabelValuePair
              value={
                <Box
                  sx={{
                    wordBreak: 'break-word',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {output.provided_input.brand?.toUpperCase() || ''}
                </Box>
              }
            />
            <Icon icon={LuX} size={18} onClick={setTooltipOpen} />
          </Box>
        )}

        {output?.opaquers_ratios?.length && (
          <>
            <Box sx={{ borderTop: '1px solid #999', width: '100%' }} />
            <PieChart
              labels={output?.opaquers}
              values={output?.opaquers_ratios}
              height={'78px'}
              width={'78px'}
            />
          </>
        )}
      </Box>
    )
  },
)

const OpaquerTooltip = React.memo(
  ({
    keyName,
    data,
    output,
    open,
    toggle,
    close,
    onDelete,
  }: {
    keyName: string
    data: any
    output: any
    open: boolean
    toggle: (key: string) => void
    close: (key: string) => void
    onDelete: (key: string) => void
  }) => {
    const [placement, setPlacement] = useState<'right' | 'left'>('right')

    const canvasImage = useAppSelector((state) => state.app.canvasImage)

    const prevOutputRef = useRef<any>(null)

    useEffect(() => {
      const currentX = data.position?.x
      const currentY = data.position?.y

      const placementResult = getTooltipPlacement({
        keyName,
        currentX,
        currentY,
        open,
        canvasImageId: canvasImage?.id,
        output,
        prevOutputRef,
        lastTooltipMap,
      })

      if (placementResult) {
        setPlacement(placementResult)
      }
    }, [
      open,
      keyName,
      data.position?.x,
      data.position?.y,
      output,
      canvasImage?.id,
    ])
    return (
      <HtmlKonvaElement key={keyName} x={data.position?.x} y={data.position?.y}>
        <Box position="relative">
          <Box sx={{ transform: 'translate(-50%, -60%)' }}>
            <Tooltip
              open={open}
              title={
                output ? (
                  <TooltipContent
                    output={output}
                    setTooltipOpen={() => close(keyName)}
                  />
                ) : null
              }
              arrow
              placement={placement}
              slotProps={{
                popper: { disablePortal: true },
                tooltip: {
                  sx: {
                    bgcolor: 'rgba(33, 33, 33, 0.80)',
                    color: 'white',
                    padding: '10px',
                  },
                },
                arrow: { sx: { color: 'rgba(33, 33, 33, 0.80)' } },
              }}
            >
              <CustomChip
                onClick={() => toggle(keyName)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'black',
                  padding: '4px 8px',
                  background: OPAQUER_BACKGROUNDS[keyName],
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: '20px',
                  '&:hover': {
                    background: OPAQUER_BACKGROUNDS[keyName],
                  },
                  '& .MuiChip-label': {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 8px 0 0px',
                    lineHeight: '20px',
                  },
                }}
                label={getInitials(data.output[keyName])}
                onDelete={() => onDelete(keyName)}
              />
            </Tooltip>
          </Box>
        </Box>
      </HtmlKonvaElement>
    )
  },
)

const KonvaOpaquer = () => {
  const dispatch = useAppDispatch()
  const opaquer = useAppSelector((state) => state.pfmCrown.opaquer)
  const opaquerRecipe = useAppSelector((state) => state.pfmCrown.opaquerRecipe)

  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  const [tooltipOpenMap, setTooltipOpenMap] = useState<Record<string, boolean>>(
    {},
  )
  useEffect(() => {
    const initialTooltipState: Record<string, boolean> = {}
    Object.keys(opaquer?.[canvasImage?.id] || {}).forEach((key) => {
      initialTooltipState[key] = true
    })
    setTooltipOpenMap(initialTooltipState)
  }, [opaquer, canvasImage?.id])

  const handleToggleTooltip = useCallback((key: string) => {
    setTooltipOpenMap((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const handleCloseTooltip = useCallback((key: string) => {
    setTooltipOpenMap((prev) => ({ ...prev, [key]: false }))
  }, [])

  const handleDeleteOpaquer = useCallback(
    (key: string) => {
      if (!canvasImage?.id) return

      dispatch(
        setOpaquerRecipeOutput({
          imageId: canvasImage?.id,
          key: key,
          result: undefined,
        }),
      )
      dispatch(
        removeOpaquerTarget({
          imageId: canvasImage?.id,
          key: key,
        }),
      )
    },
    [dispatch, canvasImage?.id],
  )
  return (
    <>
      {Object.keys(opaquer?.[canvasImage?.id] || {}).map((key) => {
        const data = opaquer?.[canvasImage?.id]?.[key]
        const output = opaquerRecipe?.[canvasImage?.id]?.[key]

        if (!data?.output || !output) return null

        return (
          <OpaquerTooltip
            key={key}
            keyName={key}
            data={data}
            output={output}
            open={!!tooltipOpenMap[key]}
            toggle={handleToggleTooltip}
            close={handleCloseTooltip}
            onDelete={handleDeleteOpaquer}
          />
        )
      })}
    </>
  )
}

export default KonvaOpaquer
